import { useMemo, useRef } from 'react';
import classNames from 'classnames';
import { atom, useAtomValue } from 'jotai';
import type { MapRef } from 'react-map-gl';
import { InlineLoading, InlineNotification } from '@carbon/react';
import bbox from '@turf/bbox';
import Map from 'components/map/map';
import { caseCoverageMapConfigAtom } from 'lib/evaluation-case/map-config';
import type {
  PackageContext,
  RunOverrides,
} from 'lib/evaluation-case/package-context';
import type { CaseFormValues } from '../types';
import {
  type ArchivedScenarioIds,
  type CaseMapState,
  footprintFeatures,
  mapDomains,
} from './coverage-geometry';
import { DEFAULT_VIEW, type Box, useCoverageFit } from './coverage-fit';
import { CoverageLayers } from './coverage-layers';
import { CoverageLegend } from './coverage-legend';
import { useCoverageGeometry } from './coverage-worker';
import {
  picksSearchParams,
  previewPicks,
  shapePicks,
  useScenarioFootprints,
} from './footprints';
import { usePackagePreview } from './coverage-preview';
import { SlotOverlay } from './slot-overlay';
import { ValuedCount } from './valued-count';
import styles from './case-coverage-map.module.scss';

interface CaseCoverageMapProps {
  /** The form's picks, unsaved ones included: the map follows them. */
  scenarios: CaseFormValues['scenarios'];
  /** The archived scenario each domain loaded with: still holding it counts as missing. */
  archivedScenarioIds?: ArchivedScenarioIds;
  /** The saved case's coverageBbox, to fit to while the footprints load. */
  coverageBbox?: Box | null;
  /** A package to add the slots of: the eval page's coverage tearsheet passes it in. */
  packageContext?: PackageContext | null;
  /** The overrides a Calculate of the eval would run with: the slots are counted under them. */
  runOverrides?: RunOverrides | null;
  /**
   * Fill the parent's height instead of the case page's fixed one (the eval page's tearsheet),
   * and re-fit whenever the map's container changes size.
   */
  fill?: boolean;
}

/**
 * The case page's coverage map (Evaluation Case Updates, Story 4, AC 6-11): one translucent red
 * footprint per picked coverage scenario — the shape-carrying five and Type Curve, whose footprint
 * is the areas it prices — all in one constant-opacity layer so the red deepens where they stack,
 * and a dashed outline framing the combined footprint. While any of the eight domains is unassigned
 * (or still the archived scenario the case loaded with) the case is unconfirmed: the stack alone,
 * no green anywhere, and the legend names the missing domains. Once all eight are assigned, the
 * region every coverage scenario reaches is cut out of the stack and drawn solid green with a solid
 * outline; the red left is where one or more scenarios don't reach, still shaded by stack depth. A
 * Type Curve that prices no area draws nothing and leaves no common area. Price Deck and DCA draw
 * nothing, shrink no green and aren't counted, though they gate the green like the rest. With
 * nothing shaped, a bare basemap and no legend.
 *
 * Given a package (the eval page's coverage tearsheet), a complete case also draws that
 * package's slots, valued or not, and counts them, from the coverage preview on the form's Type
 * Curve (else the package's): the same slots a case-level run of these picks values
 * (coverage_preview_parity_spec).
 *
 * @param {CaseCoverageMapProps} props - The picks, archived ids, saved box and package.
 * @return {JSX.Element} The map and its legend.
 */
export function CaseCoverageMap({
  scenarios,
  archivedScenarioIds = {},
  coverageBbox = null,
  packageContext = null,
  runOverrides = null,
  fill = false,
}: CaseCoverageMapProps): JSX.Element {
  // Each map's own ref: two maps on one page (the case page's and a tearsheet's) must never drive
  // each other's camera.
  const mapRefAtom = useMemo(() => atom<MapRef | null>(null), []);
  const mapRef = useAtomValue(mapRefAtom);
  // Keyed on the coverage picks themselves, not the whole form: swapping a Price Deck or DCA
  // scenario makes a new `scenarios` object but must not rerun the union, intersection and
  // difference below, which follow `picks` (and only whether a domain is left).
  const picksKey = picksSearchParams(shapePicks(scenarios)).toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const picks = useMemo(() => shapePicks(scenarios), [picksKey]);
  const { data, isError, error, isFetching, isPlaceholderData } =
    useScenarioFootprints(picks);
  // The preview counts on the Type Curve it is given, so it takes the other picks only.
  const countedPicks = useMemo(() => previewPicks(picks), [picks]);

  // Only the footprints of the current picks: the previous picks' stay drawn while the next load.
  const footprints = useMemo(() => {
    if (data?.status !== 'ready') return [];
    return data.footprints.filter(
      (footprint) => picks[footprint.domain] === footprint.scenarioId
    );
  }, [data, picks]);
  // A changed pick's footprint has not arrived: the common region would be the previous picks'.
  const loading =
    !isError &&
    (isPlaceholderData ||
      data?.status !== 'ready' ||
      footprints.length < Object.keys(picks).length);
  // The domain lists follow every pick (cheap); the geometry only whether any domain is left.
  const { missing, archived } = useMemo(
    () => mapDomains(scenarios, archivedScenarioIds),
    [scenarios, archivedScenarioIds]
  );
  const blocked = missing.length > 0 || archived.length > 0;
  const footprintCollection = useMemo(
    () => footprintFeatures(footprints),
    [footprints]
  );
  // The union, intersection and cut (computeCoverage), in a Web Worker where there is one. Until
  // its first answer the footprints are drawn uncut, with no frame or green, as 'pending'.
  // Confirmed: the green cut out of the red stack. A cut that fails keeps the whole stack and
  // draws no green, rather than green over red it couldn't clip. A union that fails outlines each
  // footprint on its own. The legend says so either way.
  const coverage = useCoverageGeometry(footprints, blocked, loading);
  const state: CaseMapState =
    coverage?.state ??
    (footprintCollection.features.length > 0 ? 'pending' : 'bare');
  const coverageFailed = coverage?.failed ?? false;
  const green = coverage?.green ?? null;
  const stackAreas = coverage?.stack ?? footprintCollection.features;
  const layers = useMemo(() => {
    const collection = (
      features: GeoJSON.Feature[]
    ): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features });
    return {
      union: collection(coverage?.outline ?? []),
      stack: collection(stackAreas),
      region: green ? collection([green]) : null,
    };
  }, [coverage, stackAreas, green]);

  // Valued slots only for a case a run would take: all eight assigned, none archived.
  const {
    preview,
    archived: previewArchived,
    ready: previewReady,
  } = usePackagePreview(
    packageContext,
    scenarios.type_curve?.id ?? null,
    countedPicks,
    !blocked,
    runOverrides
  );

  const calculating = data?.status === 'calculating' && !isError;
  const drawnCount = footprintCollection.features.length;
  const tooLarge = footprints.filter((footprint) => footprint.overCap);
  // Each assigned coverage scenario (AC 6), Type Curve included, a footprint too large to send
  // included: the legend names it below.
  const overlaidCount = drawnCount + tooLarge.length;
  const noArea = footprints.filter(
    (footprint) => !footprint.polygon && !footprint.overCap
  );
  const shaped = Object.keys(picks).length > 0;

  // The legend sits over the map's bottom-left corner, so the fit keeps clear of it. Read at fit
  // time: a legend that grows (the valued-slot key arriving) doesn't move the camera on its own.
  const legendRef = useRef<HTMLUListElement>(null);

  // Re-fits to the combined footprint as the picks change (AC 10); before the footprints arrive,
  // to the saved case's coverageBbox; with nothing shaped, back to the default view.
  const fitBox = useMemo<Box | null>(() => {
    if (drawnCount > 0) return bbox(footprintCollection) as Box;
    return calculating && coverageBbox ? coverageBbox : null;
  }, [drawnCount, footprintCollection, calculating, coverageBbox]);
  const containerRef = useRef<HTMLDivElement>(null);
  useCoverageFit({
    mapRef,
    fitBox,
    fitKey: fitBox?.join(',') ?? (shaped ? 'pending' : 'bare'),
    legendRef,
    containerRef,
    fill,
  });

  return (
    <section
      className={classNames(styles.coverage, fill && styles.fill)}
      aria-label="Coverage map"
    >
      {packageContext && shaped && (
        <ValuedCount
          missing={missing.length > 0}
          archived={archived.length > 0 || previewArchived.length > 0}
          error={preview.isError ? (preview.error ?? null) : null}
          counts={
            previewReady && {
              covered: previewReady.intersection.covered,
              total: previewReady.totalSlots,
            }
          }
        />
      )}

      <div
        ref={containerRef}
        className={classNames(
          styles.mapContainer,
          fill && styles.mapContainerFill
        )}
        data-testid="case-coverage-map"
        data-fill={fill || undefined}
      >
        <Map
          className={styles.mapFill}
          {...DEFAULT_VIEW}
          mapConfigAtom={caseCoverageMapConfigAtom}
          refAtom={mapRefAtom}
          showControls
          // The legend holds the bottom-left corner; the logo joins the attribution bottom-right.
          logoPosition="bottom-right"
        >
          <CoverageLayers
            drawn={state !== 'bare'}
            stack={layers.stack}
            region={layers.region}
            union={layers.union}
          />
          {packageContext && previewReady && (
            <SlotOverlay
              packageId={packageContext.packageId}
              typeCurveScenarioId={previewReady.typeCurveScenarioId}
              coveredSlotIds={previewReady.coveredSlotIds}
              uncoveredSlotIds={previewReady.uncoveredSlotIds}
              coveredIufsIds={previewReady.coveredIufsIds ?? []}
              uncoveredIufsIds={previewReady.uncoveredIufsIds ?? []}
              rejectedPlaces={previewReady.rejectedPlaces}
            />
          )}
        </Map>
        {shaped && (calculating || isFetching) && !isError && (
          <div className={styles.status}>
            <InlineLoading description="Calculating scenario areas..." />
          </div>
        )}

        {state !== 'bare' && (
          <CoverageLegend
            ref={legendRef}
            state={state}
            overlaidCount={overlaidCount}
            blocked={blocked}
            green={Boolean(green)}
            red={stackAreas.length > 0}
            coverageFailed={coverageFailed}
            valuedSlots={Boolean(previewReady)}
            rejectedSlots={(previewReady?.rejectedPlaces.length ?? 0) > 0}
            missing={missing}
            archived={archived}
            noAreaDomains={noArea.map((footprint) => footprint.domain)}
            tooLargeDomains={tooLarge.map((footprint) => footprint.domain)}
            typeCurveDrawn={footprints.some(
              (footprint) => footprint.domain === 'type_curve'
            )}
          />
        )}
      </div>

      {isError && (
        <InlineNotification
          kind="error"
          title="Coverage map unavailable"
          subtitle={error?.message}
          hideCloseButton
          lowContrast
        />
      )}
    </section>
  );
}
